import React, { useMemo, useRef, useEffect, useState } from "react";
import { Box, Checkbox } from "@mui/material";
import { List } from "react-window";
import ProductTableRow from "./ProductTableRow";

const DEFAULT_ROW_HEIGHT = 50;
const MAX_VISIBLE_ROWS = 15;
const rowHeightCache = {};

// react-window v2 row component — receives { index, style, ...rowProps }
const VirtualRow = ({ index, style, ariaAttributes, ...rowProps }) => {
  const rowRef = useRef(null);

  useEffect(() => {
    if (rowRef.current) {
      const height = rowRef.current.getBoundingClientRect().height;
      if (height > 0) {
        rowHeightCache[index] = height;
      }
    }
  }, [index]);
  const {
    products,
    selectedProducts,
    onProductChange,
    onVariantChange,
    onVariantsChange,
    onPageChange,
    onRemoveProduct,
    selectedClient,
    excludeIds,
    onToggleProductSelect,
    colWidths,
    hidePageColumn,
  } = rowProps;

  const entry = products[index];
  const isSelected = entry.product
    ? selectedProducts.has(entry.product._id)
    : false;
  const isLastRow = index === products.length - 1;

  return (
    <div
      ref={rowRef}
      style={{ ...style, display: "flex", width: "100%", overflow: "hidden" }}
    >
      <ProductTableRow
        entry={entry}
        entryIndex={index}
        onProductChange={onProductChange}
        onVariantChange={onVariantChange}
        onVariantsChange={onVariantsChange}
        onPageChange={onPageChange}
        onRemoveProduct={onRemoveProduct}
        selectedClient={selectedClient}
        excludeIds={excludeIds.current}
        isSelected={isSelected}
        onToggleProductSelect={onToggleProductSelect}
        colWidths={colWidths}
        isLastRow={isLastRow}
        hidePageColumn={hidePageColumn}
      />
    </div>
  );
};

const ProductsDesktopTable = ({
  products,
  onProductChange,
  onVariantChange,
  onVariantsChange,
  onPageChange,
  onRemoveProduct,
  selectedClient,
  selectedProducts,
  onToggleProductSelect,
  onSelectAllProducts,
  hidePageColumn = false,
}) => {
  const productsWithProduct = products.filter((p) => p.product);
  const allSelected =
    productsWithProduct.length > 0 &&
    productsWithProduct.every((p) => selectedProducts.has(p.product._id));
  const someSelected = productsWithProduct.some((p) =>
    selectedProducts.has(p.product._id),
  );

  const excludeIdKey = products.map((e) => e.product?._id || "").join(",");
  const excludeIds = useMemo(
    () => excludeIdKey.split(",").filter(Boolean),
    [excludeIdKey],
  );
  const excludeIdsRef = useRef(excludeIds);

  useEffect(() => {
    excludeIdsRef.current = excludeIds;
  }, [excludeIds]);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([e]) =>
      setContainerWidth(e.contentRect.width),
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Dynamic row height function - returns actual measured height or default
  const getRowHeight = (index) => {
    return rowHeightCache[index] || DEFAULT_ROW_HEIGHT;
  };

  const listHeight =
    Math.min(products.length, MAX_VISIBLE_ROWS) * DEFAULT_ROW_HEIGHT;

  // Column widths — keep in sync with header
  const colWidths = useMemo(() => {
    const checkboxW = 40;
    const pageW = hidePageColumn ? 0 : 80;
    const deleteW = 36;
    const productW = Math.round(
      (containerWidth - checkboxW - pageW - deleteW) * 0.35,
    );
    const skuW = containerWidth - checkboxW - pageW - deleteW - productW - 15;
    return { checkboxW, pageW, productW, skuW, deleteW };
  }, [containerWidth, hidePageColumn]);

  const headerCols = [
    { label: "", width: colWidths.checkboxW },
    ...(hidePageColumn ? [] : [{ label: "Page", width: colWidths.pageW }]),
    { label: "Product", width: colWidths.productW },
    { label: "SKU / Price", width: colWidths.skuW },
    { label: "", width: colWidths.deleteW },
  ];

  // rowProps are passed through to VirtualRow
  const rowProps = useMemo(
    () => ({
      products,
      selectedProducts,
      onProductChange,
      onVariantChange,
      onVariantsChange,
      onPageChange,
      onRemoveProduct,
      selectedClient,
      excludeIds: excludeIdsRef,
      onToggleProductSelect,
      colWidths,
      hidePageColumn,
    }),
    [
      products,
      selectedProducts,
      onProductChange,
      onVariantChange,
      onVariantsChange,
      onPageChange,
      onRemoveProduct,
      selectedClient,
      excludeIdsRef,
      onToggleProductSelect,
      colWidths,
      hidePageColumn,
    ],
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        border: "1px solid var(--border-color)",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          borderBottom: "1px solid var(--border-color)",
          backgroundColor: "var(--table-header-bg)",
        }}
      >
        {headerCols.map((col, i) => (
          <Box
            key={i}
            sx={{
              width: col.width,
              minWidth: col.width,
              maxWidth: col.width,
              display: "flex",
              alignItems: "center",
              justifyContent: i === 0 ? "center" : "flex-start",
              px: i === 0 ? 0 : 1,
              py: 0.5,
              color: "var(--text-tertiary)",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: "Poppins",
              borderRight:
                i < headerCols.length - 1
                  ? "1px solid var(--border-color)"
                  : "none",
              boxSizing: "border-box",
            }}
          >
            {i === 0 ? (
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={onSelectAllProducts}
                disabled={productsWithProduct.length === 0}
              />
            ) : (
              col.label
            )}
          </Box>
        ))}
      </Box>

      {/* Virtualized rows */}
      {containerWidth > 0 && (
        <List
          rowComponent={VirtualRow}
          rowCount={products.length}
          rowHeight={getRowHeight}
          rowProps={rowProps}
          overscanCount={5}
          style={{ height: listHeight }}
        />
      )}
    </Box>
  );
};

export default ProductsDesktopTable;
